import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { Layout } from './components/Layout';
import { LoginPage } from './pages/LoginPage';
import { MapaPage } from './pages/MapaPage';
import { ReservasPage } from './pages/ReservasPage';
import { ClientesPage } from './pages/ClientesPage';
import { MesasPage } from './pages/MesasPage';
import { LocaisPage } from './pages/LocaisPage';
import { ConfiguracoesPage } from './pages/ConfiguracoesPage';
import { UtilizadoresPage } from './pages/UtilizadoresPage';
import { HistoricoMesasPage } from './pages/HistoricoMesasPage';
import { PerfilPage } from './pages/PerfilPage';
import { AdminDbPage } from './pages/AdminDbPage';
import { RolesPage } from './pages/RolesPage';
import { CanaisPage } from './pages/CanaisPage';
import { EstatisticasPage } from './pages/EstatisticasPage';
import './App.css';

class ErrorBoundary extends React.Component<{ children: React.ReactNode }, { hasError: boolean; msg: string }> {
  constructor(props: { children: React.ReactNode }) { super(props); this.state = { hasError: false, msg: '' }; }
  static getDerivedStateFromError(err: Error) { return { hasError: true, msg: err.message }; }
  componentDidCatch(err: Error) { console.error('[ErrorBoundary]', err); }
  render() {
    if (this.state.hasError) return <div className="loading-screen" role="alert"><p>Algo correu mal.</p><p className="text-sm text-gray-300">{this.state.msg}</p><button className="btn btn-primary" onClick={() => location.reload()}>Recarregar</button></div>;
    return this.props.children;
  }
}

function ProtectedRoute({ children, adminOnly = false }: { children: React.ReactNode; adminOnly?: boolean }) {
  const { isAuthenticated, isAdmin, loading } = useAuth();

  if (loading) {
    return (
      <div className="loading-screen" role="status" aria-label="A carregar...">
        <div className="spinner"></div>
        <p>A carregar...</p>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (adminOnly && !isAdmin) {
    return <Navigate to="/mapa" replace />;
  }

  return <>{children}</>;
}

function AppRoutes() {
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route
        path="/*"
        element={
          <ProtectedRoute>
            <Layout />
          </ProtectedRoute>
        }
      >
        <Route index element={<Navigate to="/mapa" replace />} />
        <Route path="mapa" element={<MapaPage />} />
        <Route path="reservas" element={<ReservasPage />} />
        <Route path="clientes" element={<ClientesPage />} />
        <Route path="mesas" element={<MesasPage />} />
        <Route path="locais" element={<LocaisPage />} />
        <Route path="configuracoes" element={<ConfiguracoesPage />} />
        <Route path="historico" element={<HistoricoMesasPage />} />
        <Route path="perfil" element={<PerfilPage />} />
        <Route path="utilizadores" element={<UtilizadoresPage />} />
        <Route path="roles" element={<RolesPage />} />
        <Route path="canais" element={<CanaisPage />} />
        <Route path="estatisticas" element={<EstatisticasPage />} />
        <Route path="admin-db" element={<AdminDbPage />} />
        <Route path="*" element={<Navigate to="/mapa" replace />} />
      </Route>
      <Route path="*" element={<Navigate to="/mapa" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <ThemeProvider>
        <AuthProvider>
          <BrowserRouter>
            <a href="#main-content" className="skip-link">Saltar para conteúdo</a>
            <AppRoutes />
          </BrowserRouter>
        </AuthProvider>
      </ThemeProvider>
    </ErrorBoundary>
  );
}

export default App;